import { Image } from "@ludens-reklame/rubics-v3-sdk/file/types";
import { Link } from "@ludens-reklame/rubics-v3-sdk/link/types";
import { transformImageUrl } from "@ludens-reklame/rubics-v3-sdk/file/utils";
import {
  Constrainer,
  HorizontalSpacer,
  VerticalSpacer,
} from "../../internal-components/Layout.js";
import { RichText } from "../../internal-components/RichText.js";
import {
  ButtonAction,
  ButtonActionProps,
  ButtonActions,
} from "../../internal-components/Button.js";
import { useLinkProps } from "../../hooks/useLinkProps.js";
import { createEditableProps } from "@ludens-reklame/rubics-v3-react/util/fns";
import { TopText, Grid, Card, CardLink } from "./styles.js";
import type { BackgroundColor, MobileColumns } from "../../types/theme.js";

type ColumnSize = 2 | 3 | 4;

export interface CardGridItem {
  image?: Image;
  imageSize?: "small" | "center" | "full";
  text?: string;
  hideText?: boolean;
  link?: Link;
  actions?: ButtonActionProps[];
}

export interface CardGridProps {
  text?: string;
  backgroundColor?: BackgroundColor;
  columns?: ColumnSize;
  mobileColumns?: MobileColumns;
  list?: CardGridItem[];
}

export const CardGrid: React.FC<CardGridProps> = ({
  list = [],
  columns = 3,
  mobileColumns = 1,
  text,
  backgroundColor,
}) => {
  return (
    <VerticalSpacer>
      <Constrainer>
        <HorizontalSpacer>
          {text && (
            <TopText>
              <RichText
                dangerouslySetInnerHTML={{ __html: text }}
                {...createEditableProps("html", "text")}
              />
            </TopText>
          )}
          <Grid $columns={columns} $mobileColumns={mobileColumns}>
            {list.map((item, index) => (
              <CardItem
                key={index}
                index={index}
                {...item}

                bg={backgroundColor}
              />
            ))}
          </Grid>
        </HorizontalSpacer>
      </Constrainer>
    </VerticalSpacer>
  );
};

function CardItem({
  index,
  image,
  imageSize = "small",
  text = "",
  actions = [],
  link,
  bg,
  hideText,
}: CardGridItem & {
  index: number;
  bg?: BackgroundColor;
}) {
  const linkProps = useLinkProps(link);

  const content = (
    <>
      {image && (
        <img
          src={transformImageUrl(image, {
            width: imageSize === "full" ? 540 : undefined,
            height: imageSize === "full" ? 300 : 100,
            fit: "cover",
          })}
          alt={image.altText}
          width={imageSize === "full" ? 540 : undefined}
          height={imageSize === "full" ? 300 : 100}
        />
      )}
      {!hideText && (
        <RichText
          dangerouslySetInnerHTML={{ __html: text }}
          {...createEditableProps("html", `list.${index}.text`)}
        />
      )}
      {actions.length > 0 && (
        <ButtonActions $center={true}>
          {actions.map((action, i) => (
            <ButtonAction key={i} {...action} />
          ))}
        </ButtonActions>
      )}
    </>
  );

  if (linkProps) {
    return (
      <CardLink $bg={bg} $imageSize={imageSize} {...linkProps}>
        {content}
      </CardLink>
    );
  }

  return (
    <Card $bg={bg} $imageSize={imageSize}>
      {content}
    </Card>
  );
}
